import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';
import { filter, map, switchMap, take, timeout } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';

// Salvaguarda: tempo máximo de espera pela resposta do Firebase Auth.
// Se a aba ficar idle por um tempo antes do login, o SDK pode pendurar o
// signInWithEmailAndPassword sem nunca resolver nem rejeitar
// (GESTOR-CLIENT-TW/TX; refresh "resolve" porque reinicializa o SDK do zero).
const LOGIN_TIMEOUT_MS = 10000;

// Backoff dos retries do signIn em background. O hang pós-idle do SDK é
// intermitente: como o Firebase está fora do caminho crítico do login,
// vale re-tentar algumas vezes antes de desistir e registrar no Sentry.
const BG_SIGNIN_RETRY_DELAYS_MS = [5000, 15000, 45000];

// Tempo máximo que telas dependentes do Realtime DB aguardam a sessão
// Firebase (aberta em background após o login) antes de reportar falha.
const FIREBASE_SESSION_READY_TIMEOUT_MS = 15000;

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  uid = this.afAuth.authState.pipe(
    map(authState => authState ? authState.uid : null)
  );
  token = this.afAuth.idToken.pipe(
    map(authState => authState ? authState : null)
  );
  email = this.afAuth.authState.pipe(
    map(authState => authState ? authState.email : null)
  );
  emailVerified = this.afAuth.authState.pipe(
    map(authState => authState ? authState.emailVerified : null)
  );
  photoUrl = this.afAuth.authState.pipe(
    map(authState => authState ? authState.photoURL : null)
  );
  phoneNumber = this.afAuth.authState.pipe(
    map(authState => authState ? authState.phoneNumber : null)
  );
  isAdmin: Observable<boolean> = this.uid.pipe(
    switchMap(uid => {
      if (!uid) {
        return observableOf(false);
      } else {
        return this.db.object<boolean>('app/users/admin/' + uid).valueChanges();
      }
    })
  );
  srvId: Observable<number> = this.uid.pipe(
    switchMap(uid => {
      if (!uid) {
        return observableOf(null);
      } else {
        return this.db.object<number>(`app/users/${uid}/srvId`).valueChanges();
      }
    })
  );
  firstName: Observable<string> = this.uid.pipe(
    switchMap(uid => {
      if (!uid) {
        return observableOf(null);
      } else {
        return this.db.object<string>(`app/users/${uid}/firstName`).valueChanges();
      }
    })
  );

  // Geração da sessão de login: incrementada em logout() e a cada novo
  // signInInBackground(). As cadeias de retry capturam a geração na largada
  // e abortam quando ela muda — um retry antigo não pode reautenticar a
  // identidade anterior depois de um logout ou de outro usuário entrar.
  private sessionEpoch = 0;

  // Último signIn ACEITO (cadeia vigente no momento do resolve): permite ao
  // undo de resolve tardio distinguir "a cadeia obsoleta restabeleceu a
  // identidade antiga" de "um login mais novo abriu sessão para o MESMO
  // e-mail" — neste último caso a sessão atual é legítima e não pode cair.
  private lastAcceptedSignIn: { epoch: number; email: string } | null = null;

  constructor(
    private router: Router,
    private afAuth: AngularFireAuth,
    private db: AngularFireDatabase
  ) { }

  isLogged(): boolean {
    return this.uid !== null;
  }

  hasProfile(): Observable<boolean> {
    return new Observable<boolean>(subscribe => {
      this.srvId.subscribe(dbId => {
        subscribe.next(dbId !== null);
        // subscribe.next(true);
        subscribe.complete();
      });
    });
  }

  /**
   * Abre a sessão Firebase FORA do caminho crítico do login.
   *
   * O gate de "logado" é o token do backend (AuthApiService); a sessão
   * Firebase só alimenta recursos acessórios (ex.: Realtime DB no eigreja).
   * Como o signInWithEmailAndPassword pode pendurar após a aba ficar idle
   * (GESTOR-CLIENT-TW/TX), ele roda aqui com timeout e retries com backoff,
   * sem nunca segurar o botão "Entrando...".
   *
   * A Promise retornada nunca rejeita e existe só para testabilidade — os
   * callers devem tratá-la como fire-and-forget.
   */
  signInInBackground(email: string, senha: string): Promise<void> {
    const epoch = ++this.sessionEpoch;
    return this.trySignInWithRetry(email, senha, 0, epoch);
  }

  private trySignInWithRetry(email: string, senha: string, attempt: number, epoch: number): Promise<void> {
    // Cadeia obsoleta (houve logout ou um login mais novo): aborta antes de
    // tocar o SDK.
    if (epoch !== this.sessionEpoch) {
      return Promise.resolve();
    }

    Sentry.addBreadcrumb({
      category: 'login-flow',
      message: 'firebase-bg:attempt',
      level: 'info',
      data: { attempt },
    });

    return this.signInWithTimeout(
      email,
      senha,
      // A Promise pode expirar e o SDK completar depois mesmo assim: o undo
      // precisa alcançar também essa via out-of-band (não só o resolve
      // dentro da janela do timeout).
      () => this.undoStaleSignIn(email, attempt, epoch, 'late-after-timeout')
    ).then(() => {
      if (epoch !== this.sessionEpoch) {
        return this.undoStaleSignIn(email, attempt, epoch, 'stale-resolve');
      }

      this.lastAcceptedSignIn = { epoch, email: email.toLowerCase() };
      Sentry.addBreadcrumb({
        category: 'login-flow',
        message: 'firebase-bg:signed-in',
        level: 'info',
        data: { attempt },
      });
    }).catch((error) => {
      // Cadeia obsoleta: nem retry, nem report.
      if (epoch !== this.sessionEpoch) {
        return;
      }

      // Só re-tenta falhas transientes; erro de credencial não muda com
      // retry (e indica senha dessincronizada entre backend e Firebase).
      const transient = error?.code === 'auth/login-timeout'
        || error?.code === 'auth/network-request-failed';

      if (transient && attempt < BG_SIGNIN_RETRY_DELAYS_MS.length) {
        return new Promise<void>(resolve =>
          setTimeout(resolve, BG_SIGNIN_RETRY_DELAYS_MS[attempt])
        ).then(() => this.trySignInWithRetry(email, senha, attempt + 1, epoch));
      }

      // O backend aceitou as credenciais mas a sessão Firebase não abriu
      // (rede local bloqueando googleapis, senha dessincronizada...). O app
      // segue funcionando; registra para diagnóstico.
      Sentry.captureMessage('Firebase signIn em background falhou', {
        level: 'warning',
        tags: {
          component: 'auth-service',
          action: 'background-signin',
          error_code: error?.code ?? 'unknown',
        },
        extra: { email, attempt },
      });
    });
  }

  /**
   * Desfaz uma sessão Firebase que uma cadeia OBSOLETA restabeleceu — seja
   * por resolve dentro da janela do timeout, seja pelo completamento tardio
   * do SDK após a Promise expirar (o settled-guard descarta a Promise, mas o
   * SDK ainda seta o currentUser).
   */
  private async undoStaleSignIn(email: string, attempt: number, epoch: number, reason: string): Promise<void> {
    try {
      if (epoch === this.sessionEpoch) {
        // A cadeia ainda é a corrente: a sessão estabelecida é a desejada.
        return;
      }

      // Um login mais novo já abriu sessão para o MESMO e-mail: a sessão
      // atual é a identidade legítima — não derruba (relogin rápido).
      const accepted = this.lastAcceptedSignIn;
      if (accepted && accepted.epoch === this.sessionEpoch
        && accepted.email === email.toLowerCase()) {
        return;
      }

      // Só desfaz a sessão que esta cadeia abriu — nunca a de outro login.
      const current = await this.afAuth.currentUser;
      if (current?.email?.toLowerCase() === email.toLowerCase()) {
        await this.afAuth.signOut();
      }

      Sentry.addBreadcrumb({
        category: 'login-flow',
        message: 'firebase-bg:stale-resolve-undone',
        level: 'info',
        data: { attempt, reason },
      });
    } catch {
      // fire-and-forget: falha ao desfazer não pode propagar
    }
  }

  private signInWithTimeout(email: string, senha: string, onLateResolve?: () => void): Promise<any> {
    return new Promise((resolve, reject) => {
      let settled = false;

      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          reject({ code: 'auth/login-timeout', message: 'Tempo de resposta do login excedido.' });
        }
      }, LOGIN_TIMEOUT_MS);

      this.afAuth.signInWithEmailAndPassword(email, senha)
        .then((result) => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve(result);
          } else if (onLateResolve) {
            // A Promise já expirou, mas o SDK estabeleceu a sessão mesmo
            // assim — o caller decide se precisa desfazê-la.
            onLateResolve();
          }
        })
        .catch((error) => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            reject(error);
          }
        });
    });
  }

  /**
   * Aguarda a sessão Firebase (aberta em background pelo login backend-first)
   * estar estabelecida. Não há mais garantia de sessão antes da navegação —
   * telas que dependem do Realtime DB (dízimo/eigreja) devem aguardar aqui
   * em vez de assumir auth pronta.
   */
  firebaseSessionReady(timeoutMs: number = FIREBASE_SESSION_READY_TIMEOUT_MS): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.afAuth.authState.pipe(
        filter(user => !!user),
        take(1),
        timeout(timeoutMs)
      ).subscribe({
        next: () => resolve(true),
        error: () => resolve(false),
      });
    });
  }

  logout(): void {
    // Invalida as cadeias de signIn em background: um retry antigo não pode
    // reautenticar a identidade anterior depois do logout.
    this.sessionEpoch++;

    // A navegação não pode depender do Firebase. Após uma aba ficar inativa,
    // o signOut também pode concluir muito tarde; seu callback antigo chegava
    // a cancelar a navegação de um relogin já bem-sucedido.
    const returnUrl = this.router.url;
    this.router.navigate(['login'], {queryParams: {returnUrl}});

    this.afAuth.signOut().catch((error) => {
      console.error(`Erro ao fazer logout: ${error}`);
    });
  }
}
